import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { faSearch } from "@performyard/fontawesome/pro-regular-svg-icons";
import { InputText, Table, TableRef } from "@performyard/py-components";

import { SubsectionUtilGroup } from "../components/SubsectionUtilGroup";
import { SurveyResultsSuperRow } from "./SurveyResultsSuperRow";

import { SurveyQuestionRow } from "../types";

import { useGetSurveyQuestionsResults } from "../../../../../../../api/hooks";
import { SurveyResultsTableContext, getSurveyResultsColumns } from "../hooks/getSurveyResultsColumns";

const SUPER_ROW_ID = "__super_row__";
// Initial height estimate for ag-grid before ResizeObserver corrects it.
// Base: padding (32px) + question title (16px) + footer (24px) = 72px
// Per option: row height (20px) + gap (8px) = 28px
const SUPER_ROW_BASE_HEIGHT = 72;
const SUPER_ROW_OPTION_HEIGHT = 28;

export function SurveyResultsTable({
	surveyId,
	enableSuperRows = false,
}: {
	surveyId: string;
	enableSuperRows?: boolean;
}) {
	const [searchValue, setSearchValue] = useState("");
	const [debouncedSearchValue] = useDebounceValue(searchValue, 500);
	const [sorts, setSorts] = useState<Record<string, number>>({});
	const expandedRowIdRef = useRef<string | null>(null);
	const gridRef = useRef<TableRef<SurveyQuestionRow>>(null);
	const contextRef = useRef<SurveyResultsTableContext>({
		onExpandToggle: () => {},
		expandedRowId: null,
	});

	const {
		data: surveyQuestionsResults,
		refetch,
		isLoading,
	} = useGetSurveyQuestionsResults(surveyId, {
		...(debouncedSearchValue.length
			? {
					search: {
						field: "questions.text",
						operator: "re",
						value: debouncedSearchValue,
					},
				}
			: {}),
	});

	useEffect(() => {
		refetch();
	}, [debouncedSearchValue]);

	// Collapse super row on search change
	useEffect(() => {
		if (expandedRowIdRef.current !== null) {
			removeSuperRows();
		}
	}, [debouncedSearchValue]);

	// Stable ref — deps are [] because it only accesses refs (gridRef, expandedQuestionRef, contextRef).
	// Keeps onSortChanged and handleExpandToggle referentially stable across renders.
	const removeSuperRows = useCallback(() => {
		const api = gridRef.current?.api;
		if (!api) return;

		const superRowNode = api.getRowNode(SUPER_ROW_ID);
		if (superRowNode?.data) {
			api.applyTransaction({ remove: [superRowNode.data] });
		}
		expandedRowIdRef.current = null;
		contextRef.current.expandedRowId = null;
		api.refreshCells({ force: true });
	}, []);

	const handleExpandToggle = useCallback(
		(rowId: string) => {
			const api = gridRef.current?.api;
			if (!api) return;

			const wasExpanded = expandedRowIdRef.current === rowId;

			removeSuperRows();

			// If clicking the same row, just collapse (already removed above)
			if (wasExpanded) return;

			const rowNode = api.getRowNode(rowId);
			if (!rowNode?.data) return;

			const superRowData: SurveyQuestionRow = {
				...rowNode.data,
				_rowId: SUPER_ROW_ID,
				superRow: true,
			};

			const rowIndex = rowNode.rowIndex ?? 0;
			api.applyTransaction({ add: [superRowData], addIndex: rowIndex + 1 });
			expandedRowIdRef.current = rowId;
			contextRef.current.expandedRowId = rowId;
			api.refreshCells({ force: true });
		},
		[removeSuperRows]
	);

	const columns = getSurveyResultsColumns();

	const handleSortChanged = useCallback(() => {
		removeSuperRows();
		const api = gridRef.current?.api;
		if (!api) return;
		const columnState = api.getColumnState();
		const newSorts: Record<string, number> = {};
		for (const col of columnState) {
			if (col.sort) {
				newSorts[col.colId] = col.sort === "asc" ? 1 : -1;
			}
		}
		setSorts((prev) => {
			const prevKeys = Object.keys(prev);
			const newKeys = Object.keys(newSorts);
			if (prevKeys.length !== newKeys.length) return newSorts;
			for (const key of newKeys) {
				if (prev[key] !== newSorts[key]) return newSorts;
			}
			return prev;
		});
	}, [removeSuperRows]);

	// Keep the toggle callback up-to-date on the stable context object
	contextRef.current.onExpandToggle = handleExpandToggle;

	return (
		<div className="flex flex-col gap-4" data-testid="survey-results-table">
			<div className="flex flex-row items-center justify-between">
				<div className="w-[288px]">
					<InputText
						onChange={setSearchValue}
						placeholder="Search Questions..."
						aria-label="Search questions"
						leftIcon={faSearch}
						value={searchValue}
					/>
				</div>
				<SubsectionUtilGroup
					surveyId={surveyId}
					showSummaryCSV={enableSuperRows}
					{...(debouncedSearchValue.length
						? {
								search: {
									field: "questions.text",
									operator: "re" as const,
									value: debouncedSearchValue,
								},
							}
						: {})}
					{...(Object.keys(sorts).length ? { sorts } : {})}
				/>
			</div>
			<div className="[&_.ag-center-cols-viewport]:!min-h-0">
				<Table<SurveyQuestionRow>
					ref={gridRef}
					columnDefs={columns}
					rowData={(surveyQuestionsResults?.questions ?? []).map((question, index) => ({
						...question,
						_rowId: `q-${index}`,
					}))}
					loading={isLoading}
					autoHeight
					emptyStateText="No Questions Found"
					onSortChanged={handleSortChanged}
					getRowId={(params) => params.data._rowId}
					{...(enableSuperRows && {
						context: contextRef.current,
						isFullWidthRow: (params: { rowNode: { data?: SurveyQuestionRow } }) =>
							params.rowNode.data?.superRow === true,
						fullWidthCellRenderer: SurveyResultsSuperRow,
						getRowHeight: (params: { data?: SurveyQuestionRow }) => {
							if (!params.data?.superRow) return undefined;
							const optionsCount = params.data.options_results?.length ?? 0;
							return SUPER_ROW_BASE_HEIGHT + optionsCount * SUPER_ROW_OPTION_HEIGHT;
						},
					})}
				/>
			</div>
		</div>
	);
}
